import FilterSelect from '@Components/Filter/Dropdown';
import Image from '@Components/Image';
import { RootStateType, AppDispatch } from '@Store';
import {
  alpha,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Grid,
} from '@mui/material';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import CustomTabs from './CustomTabs';
import BarChart from './ BarChart';
import API from '@Services/apiAxios';
import { getLoggedInUserSystemList } from '../../store/reducers/Systems';
import { StatsResponse } from './types/type';
import MixedChart from './MIxedChart';
import MixedChartShipment from './MixedChartShipment';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { convertWeightKGtoLB } from '../../utils/convertWeight';

// Constants
const STAT_CARD_ICON_SIZE = { width: '60px', height: '60px' };
const STAT_CARD_IMAGE_SIZE = { width: '36px', height: '34px' };
const TREND_ICON_SIZE = { width: '10px', height: '12px' };

// Generate month options
const generateMonthOptions = (): DropdownOptionType[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const date = moment().subtract(i, 'months');
    return {
      value: date.format('MM-YYYY'),
      display: date.format('MMMM YYYY'),
    };
  });
};

const monthOptions = generateMonthOptions();

// Stat Card Configuration
const STAT_CARDS_CONFIG = [
  {
    id: 'shipment',
    icon: '/images/icons/AdminHome/shipment.png',
    alt: 'shipment',
    title: 'Shipment',
    dataKey: 'totalShipment',
    percentageKey: 'shipmentPercentage',
    description: 'Total shipments this month',
    unit: null,
  },
  {
    id: 'weight',
    icon: '/images/icons/AdminHome/weight.png',
    alt: 'weight',
    title: 'Total Weight',
    dataKey: 'totalWeight',
    percentageKey: 'weightPercentage',
    description: 'dynamic', // Will be set based on organization
    unit: 'dynamic', // Will be set based on organization
  },
  {
    id: 'revenue',
    icon: '/images/icons/AdminHome/revenue.png',
    alt: 'revenue',
    title: 'Total Revenue',
    dataKey: 'totalRevenue',
    percentageKey: 'revenuePercentage',
    description: 'dynamic', // Will be set based on currency
    unit: 'currency',
  },
];

interface StatCardProps {
  icon: string;
  alt: string;
  title: string;
  value: string | number;
  percentage: number;
  description: string;
  percentageLabel: string;
  isLoading?: boolean;
}

/**
 * Reusable StatCard component
 */
const StatCard: React.FC<StatCardProps> = ({
  icon,
  alt,
  title,
  value,
  percentage,
  description,
  percentageLabel,
  isLoading = false,
}) => {
  const isPositive = percentage >= 0;
  const trendIcon = isPositive ? '/images/icons/AdminHome/upArrow.svg' : '/images/icons/AdminHome/downArrow.svg';
  const trendColor = isPositive ? 'success.main' : 'error.main';
  const trendBgColor = isPositive ? 'success.light' : 'error.light';

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        gap: 2,
        padding: { xs: '16px', sm: '20px' },
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.2s ease-in-out',
        height: '100%',
        flexDirection: { xs: 'column', sm: 'row' },
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        },
      })}
    >
      {/* Icon Container */}
      <Box
        sx={theme => ({
          ...STAT_CARD_ICON_SIZE,
          borderRadius: '10px',
          backgroundColor: alpha(theme.palette.secondary.main, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        <Image src={icon} alt={alt} style={STAT_CARD_IMAGE_SIZE} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Trend Indicator */}
        <Typography
          variant="caption"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: trendBgColor,
            color: trendColor,
            fontWeight: 600,
            fontSize: '12px',
            mb: 1.5,
          }}
        >
          <Image src={trendIcon} alt="trend" style={TREND_ICON_SIZE} />
          {percentageLabel}
        </Typography>

        {/* Value */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '22px', sm: '24px', md: '28px' },
            lineHeight: 1.2,
            color: 'text.primary',
            mb: 0.5,
            fontFeatureSettings: '"tnum" on, "lnum" on',
            wordBreak: 'break-word',
          }}
        >
          {isLoading ? '--' : value}
        </Typography>

        {/* Title */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            color: 'text.primary',
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Stats Grid Component - Reusable for all tabs
 */
interface StatsGridProps {
  stats: StatsResponse | null;
  locale: string;
  organizationName: string;
  currency: string;
}

const StatsGrid: React.FC<StatsGridProps> = ({ stats, locale, organizationName, currency }) => {
  const cardsData = useMemo(() => {
    if (!stats?.currentMonth) return [];

    return STAT_CARDS_CONFIG.map(card => {
      let value: string | number = '--';
      let description = card.description;
      let percentageLabel = '--%';

      const cardData = stats.currentMonth;
      const dataValue = cardData[card.dataKey as keyof typeof cardData];
      const percentageValue = cardData[card.percentageKey as keyof typeof cardData] as number | undefined;

      // Format value
      if (dataValue !== undefined && dataValue !== null) {
        if (card.id === 'weight') {
          value =
            organizationName === 'Cargo Solutions Network'
              ? formatNumber(convertWeightKGtoLB(dataValue as number), locale)
              : formatNumber(dataValue as number, locale);
          description = `Total Weight ${organizationName === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`;
        } else if (card.id === 'revenue') {
          value = formatNumber(dataValue as number, locale);
          description = `Total Revenue (${currency})`;
        } else {
          value = formatNumber(dataValue as number, locale);
        }
      }

      // Format percentage label
      if (percentageValue !== undefined && percentageValue !== null) {
        percentageLabel = `${formatNumber(percentageValue, locale)}%`;
      }

      return {
        ...card,
        value,
        description,
        percentageLabel,
        percentage: percentageValue || 0,
      };
    });
  }, [stats, locale, organizationName, currency]);

  return (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 4 }}>
      {cardsData.map(card => (
        <Grid item xs={12} sm={6} md={4} key={card.id}>
          <StatCard
            icon={card.icon}
            alt={card.alt}
            title={card.title}
            value={card.value}
            percentage={card.percentage}
            description={card.description}
            percentageLabel={card.percentageLabel}
          />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Main GeneralOverview Component
 */
const GeneralOverview = () => {
  const [system, setSystem] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<{ month: string; year: string }>({
    month: moment().format('MM'),
    year: moment().format('YYYY'),
  });
  const [stats, setStats] = useState<StatsResponse | null>(null);

  const locale = useLocale();
  const dispatch = useDispatch<AppDispatch>();
  const tabLabels = ['Revenue', 'Weight', 'Shipment'];

  // Redux selectors
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name: organizationName = '' } = organizationData || {};

  // Fetch monthly stats
  const getMonthlyStats = useCallback(async () => {
    try {
      const response = await API.get('/analytics/stats/monthly', {
        params: {
          month: selectedValues.month,
          year: selectedValues.year,
          'system[]': system,
        },
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      setStats(null);
    }
  }, [selectedValues, system]);

  // Initialize system list on mount
  useEffect(() => {
    dispatch(getLoggedInUserSystemList());
  }, [dispatch]);

  // Fetch stats when filters change
  useEffect(() => {
    getMonthlyStats();
  }, [getMonthlyStats]);

  // Handle month/year selection
  const handleMonthChange = useCallback((event: SelectChangeEvent<string>) => {
    const [month, year] = event.target.value.split('-');
    setSelectedValues({ month, year });
  }, []);

  // Handle system filter
  const onSystemFilter = useCallback((selected: Array<string>) => {
    setSystem(selected);
  }, []);

  // Memoized tab content
  const tabContent = useMemo(
    () => ({
      Revenue: (
        <Box sx={{ width: '100%' }}>
          <StatsGrid
            stats={stats}
            locale={locale}
            organizationName={organizationName}
            currency={defaultOrgCountryCurrency}
          />
          <Box sx={{ mt: { xs: 2, md: 4 } }}>
            <BarChart data={stats?.graphData} />
          </Box>
        </Box>
      ),

      Weight: (
        <Box sx={{ width: '100%' }}>
          <StatsGrid
            stats={stats}
            locale={locale}
            organizationName={organizationName}
            currency={defaultOrgCountryCurrency}
          />
          <Box sx={{ mt: { xs: 2, md: 4 } }}>
            <MixedChart data={stats?.graphData} />
          </Box>
        </Box>
      ),

      Shipment: (
        <Box sx={{ width: '100%' }}>
          <StatsGrid
            stats={stats}
            locale={locale}
            organizationName={organizationName}
            currency={defaultOrgCountryCurrency}
          />
          <Box sx={{ mt: { xs: 2, md: 4 } }}>
            <MixedChartShipment data={stats?.graphData} />
          </Box>
        </Box>
      ),
    }),
    [stats, locale, organizationName, defaultOrgCountryCurrency],
  );

  return (
    <Box
      sx={{
        borderRadius: '9px',
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: '16px', sm: '20px', md: '20px 40px' },
      }}
    >
      {/* Header */}
      <Typography
        sx={{
          color: 'rgba(24, 29, 37, 1)',
          fontSize: { xs: '18px', sm: '20px', md: '24px' },
          fontWeight: 600,
          lineHeight: '33.6px',
        }}
      >
        General Overview
      </Typography>

      {/* Controls Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'stretch', sm: 'flex-end' }, // 👈 IMPORTANT
            alignItems: { sm: 'center' },
            gap: { xs: '12px', sm: '16px', md: '20px' },
            width: '100%',
          }}
        >
          {/* System Filter */}
          <Box
            sx={{
              width: { xs: '100%', sm: '220px', md: '240px' }, // 👈 fixed width
            }}
          >
            <FilterSelect
              id="system"
              label="Select system"
              options={loggedInUserSystemDropdownList}
              value={system}
              onChange={onSystemFilter}
            />
          </Box>

          {/* Month Select */}
          <Box
            sx={{
              width: { xs: '100%', sm: '220px', md: '240px' }, // 👈 remove flex:1
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="month-select-label">Select month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={`${selectedValues.month}-${selectedValues.year}`}
                onChange={handleMonthChange}
                label="Select month"
                sx={{
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  fontSize: { xs: '14px', md: '16px' },
                }}
              >
                {monthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: { xs: '14px', md: '16px' } }}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            marginTop: {
              xs: '20px',
              sm: '24px',
              md: '32px',
            },
          }}
        >
          <CustomTabs tabs={tabLabels} tabContent={tabContent} />
        </Box>
      </Box>
    </Box>
  );
};

export default GeneralOverview;
